import React, { useCallback, useRef, useState } from 'react';
import { useQuery } from '@apollo/client';
import {
  Avatar,
  Badge,
  Box,
  Chip,
  Divider,
  IconButton,
  Menu,
  MenuItem,
  MenuList,
} from '@mui/material';
import DarkModeOutlinedIcon from '@mui/icons-material/DarkModeOutlined';
import LightModeOutlinedIcon from '@mui/icons-material/LightModeOutlined';
import { Link } from 'react-router-dom';
import { AUTH_QUERY } from '../../gql';
import { useColorMode } from '../context/ColorModeContext/useColorMode';
import { UserMenuLanguageSwitcher } from '../LanguageSelector';
import { FormattedMessage, useIntl } from 'react-intl';

const avatarStyles = { width: 32, height: 32 };
const menuStyles = { width: 250 };
const spaceBetweenItemStyles = {
  display: 'flex',
  justifyContent: 'space-between',
  gap: 1,
};
const colorModeIconStyles = {
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: 20,
  height: 20,
  borderRadius: '50%',
  border: '1px solid',
  borderColor: 'divider',
  fontSize: 14,
};
const badgeAnchorOrigin = { vertical: 'bottom', horizontal: 'right' } as const;
const menuPaperProps = { sx: menuStyles };

const UserMenuButton = ({ onLogoutClick }) => {
  const intl = useIntl();
  const { data } = useQuery(AUTH_QUERY, {
    fetchPolicy: 'cache-first',
  });
  const unseen = data?.viewer?.user?.unseenChangelogCount ?? 0;
  const isAdmin = data?.viewer?.user?.role === 'admin';
  const canUsePremiumFeatures =
    isAdmin || data?.viewer?.premiumAccess?.isPremium === true;
  const { mode, toggleColorMode } = useColorMode();

  const anchorMRef = useRef<HTMLButtonElement>(null);
  const [mOpen, setMOpen] = useState(false);

  const nextMode = mode === 'light' ? 'dark' : 'light';
  const ModeIcon =
    nextMode === 'dark' ? DarkModeOutlinedIcon : LightModeOutlinedIcon;
  const handleMenuToggle = useCallback(() => {
    setMOpen((open) => !open);
  }, []);
  const handleMenuClose = useCallback(() => {
    setMOpen(false);
  }, []);

  return (
    <>
      <IconButton
        ref={anchorMRef}
        onClick={handleMenuToggle}
        size="small"
        aria-haspopup="true"
        aria-label={intl.formatMessage({
          id: 'userMenu.title',
          defaultMessage: 'User menu',
        })}
      >
        <Badge
          overlap="circular"
          color="primary"
          badgeContent="i"
          invisible={unseen === 0}
          anchorOrigin={badgeAnchorOrigin}
        >
          <Avatar sx={avatarStyles} />
        </Badge>
      </IconButton>
      <Menu
        anchorEl={anchorMRef.current}
        onClose={handleMenuClose}
        open={mOpen}
        PaperProps={menuPaperProps}
      >
        <MenuList dense>
          <MenuItem component={Link} to="/account">
            <FormattedMessage id="userMenu.account" defaultMessage="Account" />
          </MenuItem>
          <MenuItem component={Link} to="/activity">
            <FormattedMessage
              id="userMenu.activity"
              defaultMessage="Activity log"
            />
          </MenuItem>
          <MenuItem
            component={Link}
            to="/changelog"
            sx={spaceBetweenItemStyles}
          >
            <Box component="span">
              <FormattedMessage
                id="userMenu.changelog"
                defaultMessage="Changelog"
              />
            </Box>
            {unseen > 0 ? (
              <Chip label={unseen} color="primary" size="small" />
            ) : null}
          </MenuItem>
          <MenuItem component={Link} to="/contacts">
            <FormattedMessage
              id="userMenu.contacts"
              defaultMessage="Contacts"
            />
          </MenuItem>
          <MenuItem component={Link} to="/s">
            <FormattedMessage id="map.tab.sites" defaultMessage="Sites" />
          </MenuItem>
          <MenuItem component={Link} to="/field-sessions">
            <FormattedMessage
              id="userMenu.fieldSessions"
              defaultMessage="Field sessions"
            />
          </MenuItem>
          {isAdmin && (
            <MenuItem component={Link} to="/admin">
              <FormattedMessage id="userMenu.admin" defaultMessage="Admin" />
            </MenuItem>
          )}
          {canUsePremiumFeatures && (
            <>
              <MenuItem component={Link} to="/imports">
                <FormattedMessage
                  id="userMenu.imports"
                  defaultMessage="Imports"
                />
              </MenuItem>
              <MenuItem component={Link} to="/visualizer">
                <FormattedMessage
                  id="userMenu.visualizer"
                  defaultMessage="Visualizer"
                />
              </MenuItem>
            </>
          )}
          <Divider />
          <UserMenuLanguageSwitcher />
          <Divider />
          <MenuItem onClick={toggleColorMode} sx={spaceBetweenItemStyles}>
            <Box component="span">
              {nextMode === 'dark' ? (
                <FormattedMessage
                  id="userMenu.switchDark"
                  defaultMessage="Switch to dark mode"
                />
              ) : (
                <FormattedMessage
                  id="userMenu.switchLight"
                  defaultMessage="Switch to light mode"
                />
              )}
            </Box>
            <Box component="span" sx={colorModeIconStyles}>
              <ModeIcon fontSize="inherit" />
            </Box>
          </MenuItem>
          <MenuItem onClick={onLogoutClick}>
            <FormattedMessage id="userMenu.logout" defaultMessage="Log out" />
          </MenuItem>
        </MenuList>
      </Menu>
    </>
  );
};

export { UserMenuButton };
